import React from 'react';
import { Typography, Space, Tooltip, Button } from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { StatusBadge } from '../../common';
import { MAIL_VARIABLE_GROUPS, toTag } from '../../Config/mailVariables';

const { Text } = Typography;

/**
 * MailVariableCheckPanel — メールテンプレートの変数チェック結果パネル
 * ------------------------------------------------------------------
 * 件名・本文の {$変数$} を解析した結果（analyzeMailVariables の戻り値）を受け取り、
 * 定義済み / 条件付き / 未定義 / 形式エラー を StatusBadge で表示する。
 * 未定義変数には正しい変数の候補を提示し、クリックで一括置換できる。
 *
 * 配色は共用 StatusBadge の tone（success / warning / error）と tokens.css の変数のみ。
 * 新規 HEX・`.ant-*` オーバーライドは無し。
 *
 * @param {object}   props
 * @param {object}   props.analysis                analyzeMailVariables(subject, body) の結果
 * @param {function} props.onReplace(fromKey,toKey) 未定義変数を候補に一括置換する（親が件名・本文を更新）
 */
const MailVariableCheckPanel = ({ analysis, onReplace }) => {
  const { valid, conditional, unknown, malformed, total } = analysis;

  // 要約バッジ（重大なものから優先）
  let summary;
  if (malformed.length > 0) {
    summary = (
      <StatusBadge tone="error" icon={<CloseCircleOutlined />}>
        形式エラー {malformed.length}件
      </StatusBadge>
    );
  } else if (unknown.length > 0) {
    summary = (
      <StatusBadge tone="error" icon={<ExclamationCircleOutlined />}>
        未定義の変数 {unknown.length}件
      </StatusBadge>
    );
  } else if (total === 0) {
    summary = <StatusBadge tone="neutral">変数は使われていません</StatusBadge>;
  } else if (conditional.length > 0) {
    summary = (
      <StatusBadge tone="warning" icon={<InfoCircleOutlined />}>
        条件付きの変数 {conditional.length}件
      </StatusBadge>
    );
  } else {
    summary = (
      <StatusBadge tone="success" icon={<CheckCircleOutlined />}>
        問題なし
      </StatusBadge>
    );
  }

  const fieldsText = (fields) => (fields || []).join('・');

  const renderRow = (label, children) => (
    <div style={{ marginBottom: 'var(--space-2)' }}>
      <Text
        type="secondary"
        style={{
          display: 'inline-block',
          width: 88,
          fontSize: 'var(--text-xs)',
          verticalAlign: 'top',
          paddingTop: 2,
        }}
      >
        {label}
      </Text>
      <Space wrap size={[4, 6]} style={{ verticalAlign: 'top', maxWidth: 'calc(100% - 96px)' }}>
        {children}
      </Space>
    </div>
  );

  return (
    <div
      style={{
        marginTop: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        border: '1px solid var(--grey-30)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--white)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-3)',
        }}
        role="status"
        aria-live="polite"
      >
        <Text strong>■ 変数チェック</Text>
        {summary}
        {total > 0 && (
          <Text type="secondary" style={{ fontSize: 'var(--text-xs)' }}>
            件名・本文で {total} 箇所
          </Text>
        )}
      </div>

      {malformed.length > 0 &&
        renderRow(
          '形式エラー',
          malformed.map((m) => (
            <Tooltip
              key={m.raw}
              title={`${fieldsText(m.fields)}：${m.count}箇所。{$変数名$} の形式で記述してください（空白・記号は使えません）。`}
            >
              <span>
                <StatusBadge tone="error" style={{ fontFamily: 'Menlo, Consolas, monospace' }}>
                  {m.raw}
                </StatusBadge>
              </span>
            </Tooltip>
          ))
        )}

      {unknown.length > 0 &&
        renderRow(
          '未定義',
          unknown.map((u) => (
            <span key={u.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Tooltip
                title={`${fieldsText(u.fields)}：${u.count}箇所。${
                  analysis.isRevertNotify ? '回答差戻し通知' : 'サーベイお知らせ配信'
                }では置換されず、そのまま届きます。`}
              >
                <span>
                  <StatusBadge tone="error" style={{ fontFamily: 'Menlo, Consolas, monospace' }}>
                    {toTag(u.key)}
                  </StatusBadge>
                </span>
              </Tooltip>
              {u.suggestions.length > 0 && (
                <Text type="danger" style={{ fontSize: 'var(--text-xs)' }}>
                  →{' '}
                  {u.suggestions.map((s, i) => (
                    <React.Fragment key={s}>
                      {i > 0 && ' / '}
                      <Button
                        type="link"
                        size="small"
                        danger
                        style={{ padding: 0, height: 'auto', fontSize: 'var(--text-xs)' }}
                        onClick={() => onReplace && onReplace(u.key, s)}
                        title={`{$${u.key}$} を {$${s}$} に一括置換`}
                      >
                        {toTag(s)}
                      </Button>
                    </React.Fragment>
                  ))}
                  {' '}ではありませんか？（クリックで置換）
                </Text>
              )}
            </span>
          ))
        )}

      {conditional.length > 0 &&
        renderRow(
          '条件付き',
          conditional.map((c) => (
            <Tooltip
              key={c.key}
              title={`${c.label}｜${fieldsText(c.fields)}：${c.count}箇所。${
                MAIL_VARIABLE_GROUPS[c.group]?.note || ''
              }`}
            >
              <span>
                <StatusBadge tone="warning" style={{ fontFamily: 'Menlo, Consolas, monospace' }}>
                  {toTag(c.key)}
                </StatusBadge>
              </span>
            </Tooltip>
          ))
        )}

      {valid.length > 0 &&
        renderRow(
          '定義済み',
          valid.map((v) => (
            <Tooltip key={v.key} title={`${v.label}｜${fieldsText(v.fields)}：${v.count}箇所`}>
              <span>
                <StatusBadge tone="success" style={{ fontFamily: 'Menlo, Consolas, monospace' }}>
                  {toTag(v.key)}
                </StatusBadge>
              </span>
            </Tooltip>
          ))
        )}

      <Text
        type="secondary"
        style={{
          display: 'block',
          fontSize: 'var(--text-xs)',
          color: 'var(--grey-65)',
          lineHeight: 'var(--leading-normal)',
          marginTop: 'var(--space-2)',
        }}
      >
        {analysis.isRevertNotify ? (
          <>
            ※ 回答差戻し通知テンプレートです。差戻し通知で置換される変数のみを定義済みとして表示しています。
            未定義の変数は置換されずそのまま受信者に届きます。
          </>
        ) : (
          <>
            ※ 未定義の変数は、サーベイお知らせ配信では置換されずそのまま受信者に届きます。
            CSVから配信専用のテンプレートで CSV の列名を指定している場合は問題ありません。
          </>
        )}
        形式エラーはどの配信でも置換されないため、修正するまで保存できません。
      </Text>
    </div>
  );
};

export default MailVariableCheckPanel;
